import type { AdState } from '|>/core/addons/smartlib'

import videojs from 'video.js'
import { Events } from '|>/shared/events'
import { button, div, span } from '|>/shared/h'
import { absoluteTimeSeconds } from '|>/shared/utils'
import { on, register } from '|>/shared/vjs'
import { BaseComponent } from '../base'

import './skip-ad.css'

const SIZE = 36
const STROKE_WIDTH = 3
/**
 * Skip Ad component
 * Displays countdown timer and skip button for skippable ads
 */
@register
export class SkipAd extends BaseComponent {
  declare countdownEl?: HTMLElement
  declare svgEl?: SVGElement
  declare circleEl?: SVGCircleElement
  declare textEl?: HTMLElement
  declare buttonEl?: HTMLElement

  @on(Events.Media.AdStateChanged)
  private adState: AdState | null = null

  constructor(player, options) {
    super(player, options)
    this.hide()
  }

  @on(Events.Media.TimeUpdate)
  handleTimeUpdate(currentTime: number) {
    this.updateCountdown(currentTime)
  }

  override createEl() {
    const el = super.createEl()

    // Create countdown timer with circular progress
    this.countdownEl = this.createCountdownTimer()
    this.buttonEl = button('.vjs-x-skip-ad-button', {}, [
      span('.vjs-x-skip-ad-button-text', this.localize('skip_ad_button')),
      span('.vjs-icon-placeholder'),
    ])
    // Add click handler
    if (this.buttonEl) {
      this.buttonEl.setAttribute('disabled', 'true')
      videojs.on(this.buttonEl, 'click', (e: MouseEvent) => {
        this.handleClick(e)
      })
    }

    if (this.countdownEl) {
      el.appendChild(this.countdownEl)
    }
    if (this.buttonEl) {
      el.appendChild(this.buttonEl)
    }

    return el
  }

  private createCountdownTimer(): HTMLElement {
    const wrapper = div('.vjs-x-skip-ad-countdown')
    wrapper.style.display = 'none'

    // Create SVG
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('width', String(SIZE))
    svg.setAttribute('height', String(SIZE))
    svg.setAttribute('class', 'vjs-x-skip-ad-countdown-svg')
    this.svgEl = svg

    // Create progress circle
    const circle = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'circle'
    )
    const radius = (SIZE - STROKE_WIDTH) / 2
    const centerX = SIZE / 2
    const centerY = SIZE / 2
    circle.setAttribute('cx', String(centerX))
    circle.setAttribute('cy', String(centerY))
    circle.setAttribute('r', String(radius))
    circle.setAttribute('fill', 'none')
    circle.setAttribute('stroke', '#fff')
    circle.setAttribute('stroke-width', String(STROKE_WIDTH))
    circle.setAttribute('stroke-dasharray', String(2 * Math.PI * radius))
    circle.setAttribute('stroke-dashoffset', '0')
    // Rotate around center: rotate(angle, centerX, centerY)
    circle.setAttribute('transform', `rotate(-90 ${centerX} ${centerY})`)
    circle.setAttribute('class', 'vjs-x-skip-ad-countdown-circle')
    this.circleEl = circle
    svg.appendChild(circle)

    // Create text element for countdown value
    this.textEl = span('.vjs-x-skip-ad-countdown-text', '0')

    wrapper.appendChild(svg)
    if (this.textEl) {
      wrapper.appendChild(this.textEl)
    }

    return wrapper
  }

  handleClick(e: MouseEvent) {
    e?.preventDefault()
    e?.stopPropagation()
    const smartlib = this.player_.smartlib?.()
    if (smartlib && this.adState?.isAdSkippable) {
      smartlib.skip()
    }
  }

  @on(Events.Media.AdStateChanged)
  handleAdStateChange(adState: AdState) {
    if (!adState.isAdSkippable || !adState.currentAd) {
      this.hide()
      return
    }

    this.show()
  }

  private updateCountdown(currentTime: number) {
    if (!this.adState?.currentAd || !this.countdownEl || !this.buttonEl) {
      return
    }

    const skipPosition = this.adState.currentAd.skipPosition
    const remainingTime = Math.max(0, skipPosition - currentTime)
    const remainingTimeStr = absoluteTimeSeconds({
      time: (remainingTime + 1) * 1000,
    })
    const duration = skipPosition

    if (remainingTime > 0 && duration > 0) {
      // Show countdown timer
      this.countdownEl.style.display = 'flex'
      this.buttonEl.setAttribute('disabled', 'true')

      // Update text
      if (this.textEl) {
        this.textEl.textContent = remainingTimeStr
      }

      // Update circular progress
      if (this.circleEl) {
        const radius = (SIZE - STROKE_WIDTH) / 2
        const circumference = 2 * Math.PI * radius
        const progress = remainingTime / duration
        const offset = progress ? -circumference * (1 - progress) : 0

        this.circleEl.setAttribute('stroke-dashoffset', String(offset))
      }
    } else {
      // Hide countdown, enable button
      this.countdownEl.style.display = 'none'
      this.buttonEl.removeAttribute('disabled')
    }
  }
}

SkipAd.options = {
  className: 'vjs-x-skip-ad',
}
